'use client';

import { c as _c } from "react/compiler-runtime";
import { switchTestRole } from '@/app/actions';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BadgeIcon from '@mui/icons-material/Badge';
import CheckIcon from '@mui/icons-material/Check';
import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { hasCRUDError, UserRole } from '@shared/types';
import { signOut } from 'next-auth/react';
import { type FC, useState, useTransition } from 'react';
interface RoleSwitcherProps {
  currentRole: string;
  jmtAccess: boolean;
  qmtAccess: boolean;
  env: 'development' | 'production' | 'test';
}
interface RoleOption {
  role: UserRole;
  label: string;
  icon: typeof PersonIcon;
  jmtAccess: boolean;
  qmtAccess: boolean;
}
const roleOptions: RoleOption[] = [{
  role: UserRole.ADMIN,
  label: 'Admin',
  icon: AdminPanelSettingsIcon,
  jmtAccess: true,
  qmtAccess: true
}, {
  role: UserRole.USER,
  label: 'User',
  icon: PersonIcon,
  jmtAccess: true,
  qmtAccess: true
}, {
  role: UserRole.USER,
  label: 'JMT Only User',
  icon: BadgeIcon,
  jmtAccess: true,
  qmtAccess: false
}, {
  role: UserRole.SITE_MANAGER,
  label: 'Site Manager',
  icon: SupervisorAccountIcon,
  jmtAccess: true,
  qmtAccess: true
}, {
  role: UserRole.DIRECTOR,
  label: 'Director',
  icon: SupervisorAccountIcon,
  jmtAccess: true,
  qmtAccess: true
}, {
  role: UserRole.GUEST,
  label: 'Guest',
  icon: PersonOffIcon,
  jmtAccess: true,
  qmtAccess: true
}];
export const RoleSwitcher: FC<RoleSwitcherProps> = t0 => {
  const $ = _c(20);
  const {
    currentRole,
    jmtAccess,
    qmtAccess,
    env
  } = t0;
  const [anchorEl, setAnchorEl] = useState(null);
  const [isPending, startTransition] = useTransition();
  const open = Boolean(anchorEl);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = function handleClick(event) {
      setAnchorEl(event.currentTarget);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleClick = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function handleClose() {
      setAnchorEl(null);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleClose = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = async function handleRoleSwitch(option) {
      handleClose();
      startTransition(async () => {
        const result = await switchTestRole(option.role, option.jmtAccess, option.qmtAccess);
        if (hasCRUDError(result)) {
          return;
        }
        await signOut({
          callbackUrl: "/"
        });
      });
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleRoleSwitch = t3;
  const t4 = env === "test" ? "Testing" : "Development";
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      fontWeight: "bold",
      textTransform: "uppercase",
      letterSpacing: 1,
      cursor: "pointer",
      animation: "pulse 2s infinite",
      "@keyframes pulse": {
        "0%": {
          opacity: 1
        },
        "50%": {
          opacity: 0.7
        },
        "100%": {
          opacity: 1
        }
      },
      "&:hover": {
        opacity: 0.9
      }
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== isPending || $[5] !== t4) {
    t6 = <Chip label={t4} color="warning" variant="filled" size="small" onClick={handleClick} disabled={isPending} sx={t5} />;
    $[4] = isPending;
    $[5] = t4;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      vertical: "bottom",
      horizontal: "left"
    };
    t8 = {
      vertical: "top",
      horizontal: "left"
    };
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  let t9;
  if ($[9] !== currentRole || $[10] !== jmtAccess || $[11] !== qmtAccess) {
    t9 = roleOptions.map((option_0, index) => {
      const Icon = option_0.icon;
      const isActive = currentRole === option_0.role && jmtAccess === option_0.jmtAccess && qmtAccess === option_0.qmtAccess;
      return <MenuItem key={index} onClick={() => handleRoleSwitch(option_0)} selected={isActive}><ListItemIcon><Icon fontSize="small" /></ListItemIcon><ListItemText>{option_0.label}</ListItemText>{isActive && <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>}</MenuItem>;
    });
    $[9] = currentRole;
    $[10] = jmtAccess;
    $[11] = qmtAccess;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== anchorEl || $[14] !== open || $[15] !== t9) {
    t10 = <Menu anchorEl={anchorEl} open={open} onClose={handleClose} anchorOrigin={t7} transformOrigin={t8}>{t9}</Menu>;
    $[13] = anchorEl;
    $[14] = open;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t6) {
    t11 = <Box>{t6}{t10}</Box>;
    $[17] = t10;
    $[18] = t6;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  return t11;
};